import { createContext } from 'react';

// Bookmark Context
interface BookmarkContextType {
  bookmarks: string[];
  toggleBookmark: (id: string) => void;
  isBookmarked: (id: string) => boolean;
}
export const BookmarkContext = createContext<BookmarkContextType | undefined>(undefined);

// Visibility Context
interface VisibilityContextType {
  visibilitySettings: Array<{ artworkId: string; visibility: 'public' | 'private' }>;
  loading: boolean;
  error: string | null;
  updateVisibility: (itemId: string, visibility: 'public' | 'private') => Promise<void>;
  refreshVisibilitySettings: () => Promise<void>;
  getArtworkVisibility: (artworkId: string) => 'public' | 'private';
  checkAndUpdateVisibilityForPublicAttribute: (artworkId: string, hasPublicAttribute: boolean) => Promise<void>;
}
export const VisibilityContext = createContext<VisibilityContextType | undefined>(undefined);

// Theme Context
interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
}
export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Field Visibility Context (Field-level privacy controls)
export type FieldVisibility = 'public' | 'private';
export type SectionName = 'describe' | 'media' | 'measure' | 'catalogKeywords' | 'provenance' | 'additionalInfo' | 'upload';

interface FieldVisibilityContextType {
  fieldSettings: Record<string, FieldVisibility>;
  loading: boolean;
  /** True until the initial fetch of field settings completes (starts true, becomes false after first load) */
  isLoading: boolean;
  error: string | null;
  // Get visibility for a specific field (defaults to 'private')
  getFieldVisibility: (fieldPath: string) => FieldVisibility;
  // Update visibility for a single field
  updateFieldVisibility: (fieldPath: string, visibility: FieldVisibility) => Promise<void>;
  // Update visibility for multiple fields
  bulkUpdateFieldVisibility: (fields: Array<{ fieldPath: string; visibility: FieldVisibility }>) => Promise<void>;
  // Section-level operations
  makeSectionPublic: (section: SectionName) => Promise<void>;
  makeSectionPrivate: (section: SectionName) => Promise<void>;
  // Check section status
  isSectionPublic: (section: SectionName) => boolean;
  isSectionPrivate: (section: SectionName) => boolean;
  // Object-level operations (all sections at once)
  makeObjectPublic: () => Promise<void>;
  makeObjectPrivate: () => Promise<void>;
  isObjectPublic: () => boolean;
  isObjectPrivate: () => boolean;
  // Refresh settings from server
  refreshFieldVisibility: () => Promise<void>;
}
export const FieldVisibilityContext = createContext<FieldVisibilityContextType | undefined>(undefined);

// Autosave Status Context (for wizard autosave indicator)
export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'dirty';

// A flush function the active wizard tab registers so the wizard chrome can
// force an immediate save (e.g. when the user presses "Finished" before the
// 2s autosave debounce fires). ``null`` clears the registration on unmount.
export type SaveNowFn = () => Promise<void> | void;

interface AutosaveStatusContextType {
  setStatus: (status: AutosaveStatus) => void;
  setLastSaveTime: (time: Date) => void;
  registerSaveNow: (fn: SaveNowFn | null) => void;
}
export const AutosaveStatusContext = createContext<AutosaveStatusContextType>({
  setStatus: () => {},
  setLastSaveTime: () => {},
  registerSaveNow: () => {}
});
